import { createStore, combineReducers, compose, applyMiddleware } from 'redux';
import { reducer as formReducer } from 'redux-form';
import thunk from 'redux-thunk';
import authMiddleware from '@t7x000/apex-web/lib/redux/middlewares/authMiddleware.js';
import placeOrderMiddleware from '@t7x000/apex-web/lib/redux/middlewares/placeOrderMiddleware';
import startupMiddleware from '@t7x000/apex-web/lib/redux/middlewares/startupMiddleware';
import wsConnectionMiddleware from '@t7x000/apex-web/lib/redux/middlewares/wsConnectionMiddleware';
import orderFeeMiddleware from '@t7x000/apex-web/lib/redux/middlewares/orderFeeMiddleware';
import priceQuantityIncrementMiddleware from '@t7x000/apex-web/lib/redux/middlewares/priceQuantityIncrementMiddleware';
import level2monitorMiddleware from '@t7x000/apex-web/lib/redux/middlewares/level2monitorMiddleware';
import snackOverrideMiddleware from '@t7x000/apex-web/lib/redux/middlewares/snackOverrideMiddleware';
import languageMiddleware from './middlewares/languageMiddleware';
import { depositMiddleware } from './middlewares/depositMiddlewares';

// reducers (sort by name)
import api from '@t7x000/apex-web/lib/redux/reducers/apiReducer';
import apexCore from '@t7x000/apex-web/lib/redux/apexCore';
import affiliate from '@t7x000/apex-web/lib/redux/reducers/affiliateReducer';
import apiKey from '@t7x000/apex-web/lib/redux/reducers/apiKeyReducer';
import apiProcessing from '@t7x000/apex-web/lib/redux/reducers/apiProcessingReducer';
import auth from '@t7x000/apex-web/lib/redux/reducers/authReducer';
import confirmWithdraw from '@t7x000/apex-web/lib/redux/reducers/confirmWithdrawReducer';
import instrument from '@t7x000/apex-web/lib/redux/reducers/instrumentReducer';
import deposit from '@t7x000/apex-web/lib/redux/reducers/depositReducer';
import level1 from '@t7x000/apex-web/lib/redux/reducers/level1Reducer';
import level2 from '@t7x000/apex-web/lib/redux/reducers/level2Reducer';
import loyaltyFee from '@t7x000/apex-web/lib/redux/reducers/loyaltyFeeReducer';
import modal from '@t7x000/apex-web/lib/redux/reducers/modalReducer';
import orderHistory from '@t7x000/apex-web/lib/redux/reducers/orderHistoryReducer';
import paginationMemory from '@t7x000/apex-web/lib/redux/reducers/paginationReducer';
import product from '@t7x000/apex-web/lib/redux/reducers/productReducer';
import productManifest from '@t7x000/apex-web/lib/redux/reducers/productManifestReducer';
import position from '@t7x000/apex-web/lib/redux/reducers/positionReducer';
import recentTrades from '@t7x000/apex-web/lib/redux/reducers/recentTradesReducer';
import resetPassword from '@t7x000/apex-web/lib/redux/reducers/resetPasswordReducer';
import report from '@t7x000/apex-web/lib/redux/reducers/reportReducer';
import sidePane from '@t7x000/apex-web/lib/redux/reducers/sidePaneReducer';
import signup from '@t7x000/apex-web/lib/redux/reducers/signupReducer';
import snackbar from '@t7x000/apex-web/lib/redux/reducers/snackbarReducer';
import spotAccountInfo from '@t7x000/apex-web/lib/redux/reducers/spotAccountInfo';
import tickers from '@t7x000/apex-web/lib/redux/reducers/tickersReducer';
import transfer from '@t7x000/apex-web/lib/redux/reducers/transferReducer';
import transferRequest from '@t7x000/apex-web/lib/redux/reducers/transferRequestReducer';
import transferRequestRequested from '@t7x000/apex-web/lib/redux/reducers/transferRequestRequestedReducer';
import user from './reducers/userReducer';
import verifyEmail from '@t7x000/apex-web/lib/redux/reducers/verifyEmailReducer';
import verifyAddress from '@t7x000/apex-web/lib/redux/reducers/verifyAddressReducer';
import verifyDevice from '@t7x000/apex-web/lib/redux/reducers/verifyDeviceReducer';
import withdraw from '@t7x000/apex-web/lib/redux/reducers/withdrawReducer';
import wsConnection from '@t7x000/apex-web/lib/redux/reducers/wsConnectionReducer';
import eotc from '@t7x000/apex-web/lib/redux/reducers/eotcReducer';
import eotcWithdraw from '@t7x000/apex-web/lib/redux/reducers/eotcWithdrawReducer';
import eotcSettlement from '@t7x000/apex-web/lib/redux/reducers/eotcSettlementReducer';
import eotcPrices from '@t7x000/apex-web/lib/redux/reducers/eotcPriceReducer';
import { marginReducer as margin } from '@t7x000/apex-web/lib/redux/margin/reducers/marginReducer';
import { futuresReducer } from '@t7x000/apex-web/lib/redux/futures/reducers/futuresReducer';
import productAttributes from '@t7x000/apex-web/lib/redux/reducers/productAttributesReducer';
import interestBearingAccounts from '@t7x000/apex-web/lib/redux/reducers/interestBearingAccountsReducer';
import { marginNotificationsMiddleware } from '@t7x000/apex-web/lib/redux/margin/middlewares/marginNotificationsMiddleware';

const composeEnhancers =
  typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
        actionsBlacklist: ['LEVEL1_LOADED', 'LEVEL_2_UPDATES', 'REGISTER_FIELD', 'ADD_RECENT_TRADE'],
        maxAge: 200,
        name:
          process.env.NODE_ENV === 'development'
            ? 'LOCAL APEX Trading'
            : window.APEXWebConfig.global.siteName || document.title,
      })
    : compose;

const rootReducer = combineReducers({
  api,
  apexCore,
  apiKey,
  affiliate,
  auth,
  form: formReducer,
  instrument,
  level1,
  level2,
  modal,
  orderHistory,
  product,
  productManifest,
  position,
  recentTrades,
  report,
  sidePane,
  signup,
  snackbar,
  spotAccountInfo,
  user,
  apiProcessing,
  deposit,
  withdraw,
  wsConnection,
  tickers,
  transfer,
  transferRequest,
  paginationMemory,
  verifyEmail,
  verifyAddress,
  verifyDevice,
  confirmWithdraw,
  resetPassword,
  loyaltyFee,
  transferRequestRequested,
  eotc,
  eotcWithdraw,
  eotcSettlement,
  eotcPrices,
  margin,
  futures: futuresReducer,
  productAttributes,
  interestBearingAccounts,
});

const store = createStore(
  rootReducer,
  undefined,
  composeEnhancers(
    applyMiddleware(
      level2monitorMiddleware,
      priceQuantityIncrementMiddleware,
      languageMiddleware,
      authMiddleware,
      placeOrderMiddleware,
      startupMiddleware,
      wsConnectionMiddleware,
      orderFeeMiddleware,
      snackOverrideMiddleware,
      marginNotificationsMiddleware,
      depositMiddleware,
      thunk,
    ),
  ),
);

export default store;
